import * as Sentry from '@sentry/react';

/**
 * The Content-Security-Policy applied in the Electron main process
 * (shared/services/csp.ts) surfaces any blocked resource to the renderer as a
 * `securitypolicyviolation` event. We forward those to Sentry so that anything
 * the policy unexpectedly blocks in the field is caught, instead of failing
 * silently (e.g. a partner-branded font or an endpoint we didn't anticipate).
 *
 * A single blocked resource can fire the event repeatedly (once per load
 * attempt), so we de-duplicate on directive + blocked URI to avoid flooding
 * Sentry.
 */
const reportedViolations = new Set<string>();

export const initCspViolationReporting = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  document.addEventListener('securitypolicyviolation', (event) => {
    const effectiveDirective =
      event.effectiveDirective || event.violatedDirective;
    const blockedURI = event.blockedURI || 'unknown';

    const key = `${effectiveDirective}|${blockedURI}`;
    if (reportedViolations.has(key)) {
      return;
    }
    reportedViolations.add(key);

    Sentry.withScope((scope) => {
      scope.setLevel('warning');
      // Group in Sentry per directive + blocked resource, rather than by the
      // (variable) message text.
      scope.setFingerprint(['csp-violation', effectiveDirective, blockedURI]);
      scope.setExtras({
        blockedURI: event.blockedURI,
        effectiveDirective: event.effectiveDirective,
        violatedDirective: event.violatedDirective,
        disposition: event.disposition,
        documentURI: event.documentURI,
        sourceFile: event.sourceFile,
        lineNumber: event.lineNumber,
        columnNumber: event.columnNumber,
        statusCode: event.statusCode,
      });
      Sentry.captureMessage(
        `CSP violation: ${effectiveDirective} blocked ${blockedURI}`,
      );
    });
  });
};
